import { serverUrl } from '@/globals/env'
import { useAuthStore } from '@/stores/auth'
import * as Sentry from '@sentry/vue'
import { isNetworkError, retry } from '@/utils'
import RequestException from '@/exceptions/RequestException'
import UserActivityException from '@/exceptions/UserActivityException'
import NotReportableException from '@/exceptions/NotReportableException'

export const loginApi = async (data: { email: string; password: string }) => {
  try {
    const request = new Request(serverUrl + 'login', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'content-type': 'application/json'
      },
      body: JSON.stringify({ email: data.email, password: data.password })
    })

    const response = await fetch(request)

    if (!response.ok) {
      try {
        const json = await response.json()
        return { status: response.status, json }
      } catch (e) {}

      throw new RequestException(
        `${response.statusText} status: ${response.status}`,
        request,
        response
      )
    }

    const status = response.status
    const json = await response.json()
    return { status, json }
  } catch (err) {
    if (!(err instanceof UserActivityException) && !isNetworkError(err))
      Sentry.captureException(err)
    throw err
  }
}

export const registerApi = async (data: {
  name: string
  email: string
  password: string
  password_confirmation: string
  phone: string
}) => {
  try {
    const request = new Request(serverUrl + 'register', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: data.name,
        email: data.email,
        password: data.password,
        password_confirmation: data.password_confirmation,
        phone: data.phone,
        src: '_ext',
        signup_session: localStorage.getItem('signup_session') || null
      })
    })

    const response = await fetch(request)

    if (!response.ok) {
      try {
        const json = await response.json()
        return { status: response.status, json }
      } catch (e) {}

      throw new RequestException(
        `${response.statusText} status: ${response.status}`,
        request,
        response
      )
    }

    const status = response.status
    const json = await response.json()
    return { status, json }
  } catch (err) {
    if (!(err instanceof UserActivityException) && !isNetworkError(err))
      Sentry.captureException(err)
    throw err
  }
}

export const logoutApi = async () => {
  const authStore = useAuthStore()
  try {
    const options = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'content-type': 'application/json',
        Authorization: 'Bearer ' + authStore.user.token
      }
    }

    const request = new Request(serverUrl + 'logout', options)
    const response = await fetch(request)

    if (!response.ok) {
      throw new RequestException(
        `Failed to logout. ${response.statusText} status: ${response.status}`,
        request,
        response
      )
    }
  } catch (err) {
    if (!(err instanceof UserActivityException) && !isNetworkError(err))
      Sentry.captureException(err)
    throw err
  } finally {
    await authStore.destroyUserData()
  }
}

export const syncSession = async () => {
  const authStore = useAuthStore()

  try {
    if (!authStore.user?.token) return { status: '', json: {} }

    const options = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'content-type': 'application/json',
        Authorization: 'Bearer ' + authStore.user.token
      }
    }

    let response

    try {
      response = await fetch(serverUrl + 'syncSession', options)
    } catch (err) {
      if (isNetworkError(err)) {
        console.warn('Network error during syncing session.')
        return { status: 0, json: { message: 'Network error during syncing session' } }
      }

      Sentry.captureException(err)
      return { status: 500, json: { message: 'Unexpected error during syncing session' } }
    }

    // Token expired, try to refresh and retry sync
    if (response.status === 419) {
      try {
        await authStore.refreshToken()
        options.headers['Authorization'] = 'Bearer ' + authStore.user.token
        response = await fetch(serverUrl + 'syncSession', options)
      } catch (refreshErr) {
        if (
          !(
            refreshErr instanceof UserActivityException ||
            refreshErr instanceof NotReportableException ||
            refreshErr instanceof RequestException
          )
        ) {
          Sentry.captureException(refreshErr)
        }
        return {
          status: 419,
          json: { message: 'Session expired and could not be refreshed during syncing session' }
        }
      }
    }

    // Token invalid or blacklisted
    if (response.status === 401 || response.status === 404) {
      await authStore.destroyUserData()
      return { status: 401, json: { message: 'Invalid session' } }
    }

    // Other server error
    if (response.status !== 200) {
      await authStore.destroyUserData()
      throw new Error(`Failed syncing session: ${response.status}`)
    }

    const json = await response.json()
    await authStore.updateUserData(json)
    return { status: 200, json }
  } catch (error) {
    if (
      !(
        error instanceof UserActivityException ||
        error instanceof NotReportableException ||
        error instanceof RequestException
      )
    ) {
      Sentry.captureException(error)
    }
    return {
      status: 500,
      json: { message: 'Unexpected error during syncing session' }
    }
  }
}
